import * as Dialog from '@radix-ui/react-dialog';
import { RotateCcw, RotateCw, ScanSearch, Sparkles } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Corners, Enhancements, RenderParams } from '@/tools/image-engine';
import { AiEnhanceDialog } from '@/tools/images/AiEnhanceDialog';
import { CornerEditor } from '@/tools/images/CornerEditor';
import { enhanceWithAi } from '@/tools/images/enhance-client';
import { LivePreview } from '@/tools/images/LivePreview';
import { DEFAULT_PRESET, PRESETS, presetById, type PresetId } from '@/tools/images/presets';
import { useImageSession } from '@/tools/images/use-image-session';
import { TOOLS_AI_ENHANCE, holds, useToolsPermissions } from '@/tools/use-tools-permissions';
import { pageFromDataUrl, type PageImage } from './document';

type Tab = 'straighten' | 'correct';
type AiState = 'idle' | 'confirming' | 'working' | 'failed' | 'unavailable';

/**
 * One page of the expense's document, through the Immagini tool's own parts (#387): corners with
 * the loupe, 90° rotation, the one-tap corrections, and «Migliora con IA» to whoever already has it
 * there (`tools:ai-enhance`). «Fatto» replaces the page with the result; «Annulla» keeps it as it was.
 */
export function PageEditorDialog({
  page,
  index,
  onDone,
  onCancel,
}: {
  readonly page: PageImage;
  readonly index: number;
  readonly onDone: (next: PageImage) => void;
  readonly onCancel: () => void;
}) {
  const { t } = useTranslation(['expenses', 'tools']);
  const editor = usePageEditor(page, onDone, onCancel);
  const { session, params, tab, setTab, aiResult, setAiResult, presetId, ai, setAi, busy } = editor;
  const { canEnhance, choosePreset, sendToAi, finish } = editor;

  return (
    <Dialog.Root open onOpenChange={(open) => (open ? undefined : onCancel())}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[var(--scrim)]" />
        <Dialog.Content
          className="fixed inset-0 z-50 flex flex-col gap-4 overflow-y-auto bg-card p-4 sm:inset-6
            sm:rounded-xl sm:border sm:border-border sm:p-6 sm:shadow-lg"
        >
          <EditorHeader index={index} tab={tab} onTab={setTab} />
          <Dialog.Description className="sr-only">{t('capture.pages.hint')}</Dialog.Description>

          {!session.image || !session.corners ? (
            <p className="text-sm text-muted-foreground">{t('capture.pages.editor.working')}</p>
          ) : (
            <EditorBody
              tab={tab}
              session={session}
              source={session.image.source}
              corners={session.corners}
              params={params}
              aiResult={aiResult}
              onCorners={(next) => {
                session.setCorners(next);
                setAiResult(null);
              }}
              presetId={presetId}
              onPreset={choosePreset}
              canEnhance={canEnhance}
              ai={ai}
              onAskAi={() => setAi('confirming')}
            />
          )}

          <div className="mt-auto flex justify-end gap-2 border-t border-border pt-4">
            <Button type="button" variant="outline" onClick={onCancel}>
              {t('capture.pages.editor.cancel')}
            </Button>
            <Button
              type="button"
              disabled={busy || !session.corners || ai === 'working'}
              onClick={() => void finish()}
            >
              {t('capture.pages.editor.done')}
            </Button>
          </div>
          {/* Inside the content, so Radix stacks it over this dialog. */}
          <AiEnhanceDialog
            open={ai === 'confirming'}
            onCancel={() => setAi('idle')}
            onSend={() => void sendToAi()}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** The editor's session, its settings, and the two ways out: the page as edited, or as it was. */
function usePageEditor(page: PageImage, onDone: (next: PageImage) => void, onCancel: () => void) {
  const session = useImageSession();
  const canEnhance = holds(useToolsPermissions(), TOOLS_AI_ENHANCE);
  const [tab, setTab] = useState<Tab>('straighten');
  const [presetId, setPresetId] = useState<PresetId>(DEFAULT_PRESET);
  const [settings, setSettings] = useState<Enhancements>(presetById(DEFAULT_PRESET).settings);
  const [ai, setAi] = useState<AiState>('idle');
  const [aiResult, setAiResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { load } = session;

  useEffect(() => {
    let cancelled = false;
    void fetch(page.dataUrl)
      .then((response) => response.blob())
      .then((blob) => {
        if (!cancelled) return load(new File([blob], page.name, { type: blob.type }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
    // Loads the page once, when the dialog opens on it.
  }, [page.id]);

  const mode = presetById(presetId).mode;
  const params: RenderParams | null = useMemo(
    () => (session.corners ? { corners: session.corners, rotation: 0, settings, mode } : null),
    [session.corners, settings, mode],
  );

  const choosePreset = (id: PresetId): void => {
    setPresetId(id);
    setSettings(presetById(id).settings);
    setAiResult(null);
  };

  const sendToAi = async (): Promise<void> => {
    setAi('working');
    const jpeg = await session.exportJpeg({ rotation: 0, settings, mode });
    const outcome = jpeg ? await enhanceWithAi(jpeg) : ({ kind: 'failed' } as const);
    if (outcome.kind === 'ok') {
      setAiResult(outcome.dataUrl);
      setAi('idle');
    } else {
      setAi(outcome.kind);
    }
  };

  const finish = async (): Promise<void> => {
    setBusy(true);
    try {
      const dataUrl = aiResult ?? (await session.exportJpeg({ rotation: 0, settings, mode }));
      if (dataUrl) onDone(await pageFromDataUrl(dataUrl, page.name));
      else onCancel();
    } finally {
      setBusy(false);
    }
  };
  return {
    session,
    params,
    tab,
    setTab,
    aiResult,
    setAiResult,
    presetId,
    ai,
    setAi,
    busy,
    canEnhance,
    choosePreset,
    sendToAi,
    finish,
  };
}

function EditorHeader({
  index,
  tab,
  onTab,
}: {
  readonly index: number;
  readonly tab: Tab;
  readonly onTab: (tab: Tab) => void;
}) {
  const { t } = useTranslation(['expenses', 'tools']);
  return (
    <div className="flex items-center justify-between gap-2">
      <Dialog.Title className="text-lg font-semibold">
        {t('capture.pages.editor.title', { n: index + 1 })}
      </Dialog.Title>
      <div role="tablist" className="flex gap-1">
        {(['straighten', 'correct'] as const).map((id) => (
          <Button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            variant={tab === id ? 'default' : 'outline'}
            size="sm"
            className="h-11 min-h-11"
            onClick={() => onTab(id)}
          >
            {t(`capture.pages.editor.${id}`)}
          </Button>
        ))}
      </div>
    </div>
  );
}

type EditorBodyProps = {
  readonly tab: Tab;
  readonly session: ReturnType<typeof useImageSession>;
  readonly source: NonNullable<ReturnType<typeof useImageSession>['image']>['source'];
  readonly corners: Corners;
  readonly params: RenderParams | null;
  readonly aiResult: string | null;
  readonly onCorners: (next: Corners) => void;
  readonly presetId: PresetId;
  readonly onPreset: (id: PresetId) => void;
  readonly canEnhance: boolean;
  readonly ai: AiState;
  readonly onAskAi: () => void;
};

function EditorBody(props: EditorBodyProps) {
  return props.tab === 'straighten' ? <StraightenPanel {...props} /> : <CorrectPanel {...props} />;
}

function StraightenPanel({ session, source, corners, params, onCorners }: EditorBodyProps) {
  const { t } = useTranslation(['expenses', 'tools']);
  return (
    <div className="grid min-h-0 gap-4 lg:grid-cols-2">
      <div className="min-w-0 space-y-2">
        <CornerEditor
          source={source}
          corners={corners}
          onChange={(next, final) => {
            if (final) onCorners(next);
          }}
        />
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('tools:images.straighten.rotateLeft')}
            onClick={() => void session.rotateQuarter(-1)}
          >
            <RotateCcw />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={t('tools:images.straighten.rotateRight')}
            onClick={() => void session.rotateQuarter(1)}
          >
            <RotateCw />
          </Button>
          <Button type="button" variant="outline" onClick={() => void session.detect()}>
            <ScanSearch aria-hidden="true" />
            {t('tools:images.straighten.detect')}
          </Button>
        </div>
      </div>
      <LivePreview
        engine={session.engine}
        params={params}
        label={t('tools:images.straighten.preview')}
        className="block h-auto max-h-[60vh] w-full rounded-md border border-border object-contain"
      />
    </div>
  );
}

function CorrectPanel({
  session,
  params,
  aiResult,
  presetId,
  onPreset,
  canEnhance,
  ai,
  onAskAi,
}: EditorBodyProps) {
  const { t } = useTranslation(['expenses', 'tools']);
  return (
    <div className="grid min-h-0 gap-4 lg:grid-cols-2">
      {aiResult ? (
        <img
          src={aiResult}
          alt={t('tools:images.ai.improved')}
          className="block h-auto max-h-[60vh] w-full rounded-md border border-border object-contain"
        />
      ) : (
        <LivePreview
          engine={session.engine}
          params={params}
          label={t('tools:images.straighten.preview')}
          className="block h-auto max-h-[60vh] w-full rounded-md border border-border object-contain"
        />
      )}
      <div className="space-y-4">
        <div
          role="group"
          aria-label={t('tools:images.correct.label')}
          className="grid grid-cols-2 gap-2"
        >
          {PRESETS.map((preset) => (
            <Button
              key={preset.id}
              type="button"
              variant="outline"
              aria-pressed={presetId === preset.id}
              onClick={() => onPreset(preset.id)}
              className={cn(
                'h-11',
                presetId === preset.id && 'border-ring bg-selected text-selected-foreground',
              )}
            >
              {t(`tools:images.correct.presets.${preset.id}`)}
            </Button>
          ))}
        </div>
        {canEnhance && (
          <div className="space-y-2 rounded-lg bg-selected p-4">
            <p className="text-sm text-muted-foreground">{t('tools:images.ai.text')}</p>
            <Button
              type="button"
              variant="outline"
              disabled={ai === 'working'}
              onClick={() => onAskAi()}
            >
              <Sparkles aria-hidden="true" />
              {ai === 'working' ? t('tools:images.ai.working') : t('tools:images.ai.button')}
            </Button>
            {aiResult && (
              <p role="note" className="text-sm font-medium">
                {t('tools:images.ai.caution')}
              </p>
            )}
            {(ai === 'failed' || ai === 'unavailable') && (
              <p role="alert" className="text-sm text-destructive">
                {t(`tools:images.ai.${ai}`)}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
